import { createBrowserClient } from "@supabase/ssr";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { withBase } from "./base";
import { normalizeProgress } from "./progress";
import type { AuthUser, LearnerProgress } from "../types";

export type PublicConfig = { supabaseUrl: string; supabasePublishableKey: string; authEnabled: boolean };

let client: SupabaseClient | null = null;

export async function loadPublicConfig(): Promise<PublicConfig> {
  const fallback: PublicConfig = { supabaseUrl: "", supabasePublishableKey: "", authEnabled: false };
  try {
    const response = await fetch(withBase("/api/config"));
    if (!response.ok) return fallback;
    const config = await response.json() as Partial<PublicConfig>;
    return {
      supabaseUrl: typeof config.supabaseUrl === "string" ? config.supabaseUrl : "",
      supabasePublishableKey: typeof config.supabasePublishableKey === "string" ? config.supabasePublishableKey : "",
      authEnabled: Boolean(config.authEnabled),
    };
  } catch {
    // In local Vite dev (no Worker) /api/config returns the SPA shell, not JSON.
    // Fall back to guest mode instead of throwing an unhandled rejection.
    return fallback;
  }
}

/**
 * Sign-in is owned by Al Noor Tuition, not by this app.
 *
 * `createBrowserClient` (@supabase/ssr) keeps the session in cookies under the
 * same `sb-<project-ref>-auth-token` key the LMS writes from the server. Because
 * this app is served from a path on the same origin
 * (www.alnoortuition.com/alquran), a learner who is signed in to the LMS is
 * already signed in here — and signing out here signs them out everywhere.
 * That is deliberate: one account, one session, no second password database.
 *
 * The plain `createClient` used before kept its own localStorage session, which
 * would have asked the same person to log in twice on the same domain.
 */
export function configureAuth(config: PublicConfig) {
  if (!config.authEnabled) return null;
  client ??= createBrowserClient(config.supabaseUrl, config.supabasePublishableKey);
  return client;
}

export function authClient() {
  return client;
}

/**
 * Where an unauthenticated learner goes to sign in, returning here afterwards.
 * `next` may carry a query (a class invite), which the LMS's safeNext accepts
 * for any same-site path.
 */
export function loginUrl(next = withBase("/")) {
  return `/lms/login?next=${encodeURIComponent(next)}`;
}

export async function fetchAppUser(session: Session): Promise<AuthUser | null> {
  const response = await fetch(withBase("/api/me"), { headers: { Authorization: `Bearer ${session.access_token}` } });
  if (!response.ok) return null;
  return response.json() as Promise<AuthUser>;
}

export async function loadCloudJourney(session: Session): Promise<LearnerProgress | null> {
  const response = await fetch(withBase("/api/journey"), { headers: { Authorization: `Bearer ${session.access_token}` } });
  if (!response.ok) return null;
  const body = await response.json() as { progress?: unknown };
  // Normalise rather than cast. `as LearnerProgress` is a claim about a value
  // that came off the network, and it was a false one for every journey saved
  // before a field was added — which reached mergeProgress and took the whole
  // app down. A cast asserts a shape; this one actually produces it.
  return normalizeProgress(body.progress as Partial<LearnerProgress>);
}

export async function saveCloudJourney(session: Session, progress: LearnerProgress) {
  const response = await fetch(withBase("/api/journey"), {
    method: "PUT",
    headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ progress }),
  });
  if (!response.ok) throw new Error("Your cloud journey could not be saved.");
}
